import { useEffect, useState } from "react";
import { api } from "@/helper/api";

export interface BookableAppointmentSubject {
  id: string;
  name: string;
  is_analog: boolean;
}

const PAGE_SIZE = 50;

export function useBookingSubjects(open: boolean, requestId: string) {
  const [subjects, setSubjects] = useState<BookableAppointmentSubject[]>([]);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!open) return;
    let disposed = false;
    setLoading(true);
    setFailed(false);
    const timer = window.setTimeout(() => {
      api.genericApiRequest({
        entity: "appointmentRequest",
        entityId: requestId,
        method: "get",
        additionalRouteParts: { booking_subjects: "/" },
        parametersToRender: {
          limit: PAGE_SIZE,
          offset,
          search: encodeURIComponent(search),
        },
        successHandler: (res: {
          data: { results: BookableAppointmentSubject[]; next: string | null };
        }) => {
          if (disposed) return;
          setSubjects((current) =>
            offset === 0 ? res.data.results : [...current, ...res.data.results],
          );
          setHasMore(Boolean(res.data.next));
          setLoading(false);
        },
        failHandler: () => {
          if (disposed) return;
          setFailed(true);
          setLoading(false);
        },
      });
    }, 200);
    return () => {
      disposed = true;
      window.clearTimeout(timer);
    };
  }, [open, requestId, search, offset, reload]);

  const changeSearch = (value: string) => {
    if (value === search) return;
    setSearch(value);
    setOffset(0);
    setSubjects([]);
    setHasMore(false);
  };

  return {
    subjects,
    search,
    loading,
    failed,
    hasMore,
    changeSearch,
    loadMore: () => setOffset(subjects.length),
    retry: () => setReload((value) => value + 1),
  };
}
